import { createFileRoute } from "@tanstack/react-router";
import { PHOTOGRAPHER } from "@/content/foto";
import { LandingPage, type LandingFAQ } from "@/components/site/landing/LandingPage";
import { FotoQuoteForm } from "@/components/site/foto/FotoQuoteForm";
import { PhotoMasonry } from "@/components/site/foto/PhotoMasonry";
import { coverPhotoFor, galleryPhotos } from "@/content/photo-gallery";
import {
  aeoGeo,
  breadcrumbs,
  faqPage,
  imageGallery,
  qaPage,
  person,
  speakable,
} from "@/lib/seo/schema";

const COVER = coverPhotoFor("vad-kostar-en-fotograf");

const PATH = "/foto/vad-kostar-en-fotograf";
const TITLE = "Vad kostar en fotograf? Så sätts priset 2026 | Fenny";
const DESCRIPTION =
  "Vad påverkar priset när ett företag anlitar fotograf? Tid, antal bilder, ljussättning, retusch och användningsrätt — och varför vi offererar per uppdrag.";

const SHORT_ANSWER =
  "Priset för en företagsfotograf styrs av fem faktorer: tid på plats, antal färdiga bilder, ljussättning (befintligt ljus eller riggad studio), omfattning på retusch samt hur brett bilderna ska användas. Fenny sätter inga fasta paketpriser — varje uppdrag offereras utifrån dessa fem faktorer och ni får ett fast pris innan dagen bokas.";

const FAQ: LandingFAQ[] = [
  {
    q: "Varför har ni inte fast prislista?",
    a: "För att två uppdrag med samma rubrik kan skilja fem gånger i arbete. Tio porträtt mot vit botten och tio miljöporträtt på fyra våningsplan är inte samma jobb. En prislista skulle antingen överdebitera det enkla uppdraget eller underleverera på det svåra. Vi räknar i stället på ert uppdrag och låser priset innan start.",
  },
  {
    q: "Vad kostar en halvdag respektive heldag?",
    a: "Det beror på uppdraget. Halvdag räcker till ett porträttpass eller ett mindre eventpass; heldag krävs för större porträttdagar, kampanj och serieproduktion av produkter. Ni får exakt pris i offerten inom en arbetsdag.",
  },
  {
    q: "Tillkommer kostnader efteråt?",
    a: "Nej. Offerten specificerar vad som ingår: tid, antal bilder, retuschnivå, leveransformat och användningsrätt. Tillkommer något under vägen stäms det av och godkänns av er innan vi utför det.",
  },
  {
    q: "Ingår resa?",
    a: "Resa inom Storstockholm ingår. Resor till andra orter specificeras separat i offerten, och vid längre resor lönar det sig ofta att samla flera uppdrag på samma dag.",
  },
  {
    q: "Vad menas med användningsrätt och varför påverkar den priset?",
    a: "Användningsrätt reglerar var, hur länge och i vilka kanaler bilderna får användas. Bilder som bara ska ligga på er sajt är billigare än samma bilder i nationell utomhusannonsering under två år, eftersom värdet och eventuella modellersättningar skiljer sig kraftigt.",
  },
  {
    q: "Blir det billigare med fler bilder?",
    a: "Ja, styckpriset sjunker. Uppsättning, resa och riggning betalas en gång; varje ytterligare porträtt eller artikel kostar bara den extra tiden och retuschen.",
  },
];

export const Route = createFileRoute("/foto/vad-kostar-en-fotograf")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: "Vad kostar en fotograf? — Fenny" },
      { property: "og:description", content: "Fem faktorer som avgör priset när företag anlitar fotograf." },
      { property: "og:type", content: "article" },
      { property: "og:url", content: `https://fenny.se${PATH}` },
    ],
    links: [{ rel: "canonical", href: `https://fenny.se${PATH}` }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(aeoGeo({
            path: PATH,
            name: TITLE,
            description: DESCRIPTION,
            question: "Vad kostar en fotograf för företag?",
            answer:
              "Priset för en företagsfotograf styrs av fem faktorer: tid på plats, antal färdiga bilder, ljussättning, retuschnivå och användningsrätt. Fenny sätter inga fasta paketpriser — varje uppdrag offereras utifrån dessa faktorer och ni får ett fast pris innan dagen bokas, med svar inom en arbetsdag. Kontakt: Fenny, hey@fenny.se.",
            speakableSelectors: ["[data-quick-answer]"],
          })) },
      { type: "application/ld+json", children: JSON.stringify(speakable([".lede"])) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          qaPage({
            question: "Vad kostar en fotograf för företag?",
            answer: SHORT_ANSWER,
            path: PATH,
          }),
        ),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          person({
            name: PHOTOGRAPHER.name,
            jobTitle: PHOTOGRAPHER.role,
            email: PHOTOGRAPHER.email,
            path: PATH,
            description: PHOTOGRAPHER.bio,
            knowsAbout: [
              "Fotografpriser",
              "Offert fotograf",
              "Företagsfotografering",
              "Användningsrätt bilder",
            ],
          }),
        ),
      },
      { type: "application/ld+json", children: JSON.stringify(faqPage(FAQ)) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          imageGallery({
            name: "Exempel på företagsfoto från Fenny",
            path: PATH,
            description:
              "Porträtt, kampanjbilder, produktfoto och studiobilder ur uppdrag som ligger till grund för hur vi prissätter fotografering.",
            images: galleryPhotos.slice(0, 16),
          }),
        ),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          breadcrumbs([
            { name: "Hem", path: "/" },
            { name: "Foto", path: "/foto" },
            { name: "Vad kostar en fotograf", path: PATH },
          ]),
        ),
      },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <LandingPage
      heroImage={{ src: COVER.src, alt: COVER.alt }}
      eyebrow="Pris och offert"
      title={<>Vad kostar en fotograf? Fem faktorer avgör.</>}
      lede="Vi säljer inte fotopaket. I stället räknar vi på ert uppdrag och låser priset innan dagen bokas. Här är exakt vad vi räknar på — så att ni kan bedöma vilken offert som helst, inte bara vår."
      tldr={<p>{SHORT_ANSWER}</p>}
      ctaPrimary={{ label: "Begär offert", to: "#offert" }}
      ctaSecondary={{ label: "Företagsfotografering", to: "/foto/foretagsfotografering" }}
      trackingId="foto_vad_kostar_en_fotograf"
      stills={false}
      videos={false}
      afterHero={
        <div className="py-12">
          <PhotoMasonry
            photos={galleryPhotos.slice(0, 16)}
            filterable={false}
            initial={12}
            eyebrow="Exempel"
            heading="Så ser bilderna ut som vi prissätter"
          />
        </div>
      }
      bullets={[
        { title: "Fast pris innan start", body: "Ingen öppen timdebitering och inga efterhandsöverraskningar." },
        { title: "Specificerad offert", body: "Tid, antal bilder, retusch, format och användningsrätt står svart på vitt." },
        { title: "Svar inom en arbetsdag", body: "Skicka uppdraget så återkommer Fenny med upplägg och pris." },
      ]}
      sections={[
        {
          id: "fem-faktorer",
          heading: "De fem faktorerna",
          body: (
            <>
              <p>
                <strong>1. Tid på plats.</strong> Halvdag eller heldag, och hur
                mycket riggning uppdraget kräver. Riggning av studio tar cirka 45
                minuter i varje ände.
              </p>
              <p>
                <strong>2. Antal färdiga bilder.</strong> Det är efterarbetet som
                kostar, inte exponeringarna. Fyrtio retuscherade bilder tar
                betydligt längre tid än tio.
              </p>
              <p>
                <strong>3. Ljussättning.</strong> Fotografering i befintligt ljus
                är snabbast. Riggad studio ger kontroll och enhetlighet men tar
                tid att bygga.
              </p>
              <p>
                <strong>4. Retuschnivå.</strong> Färgsättning och varsam retusch
                ingår alltid. Frilägg, kompositioner och tung produktretusch
                debiteras per bild.
              </p>
              <p>
                <strong>5. Användningsrätt.</strong> Egna kanaler är standard.
                Köpt media, utomhus och tryck i stor upplaga påverkar priset,
                särskilt när modeller medverkar.
              </p>
            </>
          ),
        },
        {
          id: "sanka-kostnaden",
          heading: "Tre sätt att sänka kostnaden utan att sänka kvaliteten",
          body: (
            <>
              <p>
                <strong>Samla uppdragen.</strong> Porträtt, miljöbilder och
                produktfoto på samma dag delar riggning och resa.
              </p>
              <p>
                <strong>Kombinera med film.</strong> Ligger en filminspelning
                ändå i kalendern är stillbilder ett litet tillägg. Se{" "}
                <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/foto/foto-och-film">
                  foto och film samtidigt
                </a>
                .
              </p>
              <p>
                <strong>Var tydlig med användningen.</strong> Betala inte för
                rättigheter ni inte behöver. Ska bilderna bara ligga på sajten
                och LinkedIn ska offerten säga det.
              </p>
            </>
          ),
        },
        {
          id: "jamfor-offerter",
          heading: "Så jämför ni två offerter rättvist",
          body: (
            <>
              <p>
                Kontrollera fyra rader: antal levererade bilder, retuschnivå,
                leveranstid och användningsrätt. En offert som ser billigare ut men
                levererar tio bilder i stället för fyrtio, med begränsad
                användningsrätt, är i praktiken dyrare per användbar bild.
              </p>
              <p>
                Vill ni ha referensnivåer att jämföra mot publicerar vi öppna
                marknadssiffror i{" "}
                <a
                  className="underline decoration-mint/40 underline-offset-4 hover:text-mint"
                  href="/branschstudier/bildindex-2026"
                >
                  Bildindex 2026
                </a>{" "}
                — paketpris, leveranstid och regionalt prisindex per fototjänst,
                fritt att citera.
              </p>
            </>
          ),
        },
      ]}
      faq={FAQ}
      riskReducers
      closing={{
        title: "Vill ni ha ett pris på ert uppdrag?",
        body: "Beskriv uppdraget i formuläret så får ni ett specificerat fast pris inom en arbetsdag.",
        primary: { label: "Begär offert", to: "#offert" },
      }}
      contactSlot={<FotoQuoteForm location="foto_pris" />}
    />
  );
}
